import { ProductOptionGroupRecord } from "common/records/ProductOptionGroup";
import { ProductOptionRecord } from "common/records/ProductOption";
import { EstimateLineItemOptionRecord } from "common/records/EstimateLineItemOption";
import { RootState } from "common/reducers";
import { Nullable } from "common/types";
import { List, Map } from "immutable";
import { createSelector } from "reselect";
import { productOverviewById } from "common/selectors/product.selectors";
import { byId as productOptionsById } from "common/selectors/productOption.selectors";
import { byId as estimateLineItemOptionById } from "common/selectors/estimateLineItemOption.selectors";

const model = "productOptionGroups";

const EMPTY_OPTIONS = List<ProductOptionRecord>();

/**
 * Loads the option-group `byId` map from the activated-price-list snapshot store.
 *
 * @param {RootState} state The RootState
 * @returns {Map<number, ProductOptionGroupRecord>} The `byId` map
 */
export const byId = (state: RootState): Map<number, ProductOptionGroupRecord> => state.getIn([model, "byId"]);

/**
 * Loads the option group with the given id; `undefined` when the id is absent from the snapshot store.
 *
 * @param {RootState} state The RootState
 * @param {number} id The option group id
 * @returns {Nullable<ProductOptionGroupRecord>} The option group, or `undefined` if not found
 */
export const productOptionGroupById = (state: RootState, id: number): Nullable<ProductOptionGroupRecord> =>
  byId(state).get(id);

/**
 * Loads the editor-eligible option groups for a product, resolved from the activated-price-list
 * snapshot stores via the product's `option_group_ids`. Filtered to default-type groups
 * (`pog_type === "default" || !pog_type`) — `"cost"`-type groups are excluded from the editor tabs —
 * and returned in `option_group_ids` order. Unresolved ids (absent from the snapshot store) are dropped.
 *
 * @param {RootState} state The RootState
 * @param {{ productId: Nullable<number> }} props The product id whose option groups to load
 * @returns {List<ProductOptionGroupRecord>} The ordered list of default-type option groups
 */
export const defaultProductOptionGroupsForProduct = createSelector(
  [
    byId,
    (state: RootState, props: { productId: Nullable<number> }) =>
      props.productId ? productOverviewById(state, props.productId) : null,
  ],
  (groupsById, product): List<ProductOptionGroupRecord> => {
    if (!product) {
      return List<ProductOptionGroupRecord>();
    }

    return product.option_group_ids
      .map((id) => groupsById.get(id))
      .filter(
        (group): group is ProductOptionGroupRecord =>
          group != null && (group.pog_type === "default" || !group.pog_type),
      );
  },
);

/**
 * Resolves the options for a single option group, in `option_ids` order, from the activated-price-list
 * snapshot store. Unresolved ids (absent from the snapshot store) are dropped.
 *
 * Memoized over stable store slices (the group `byId` map, the option `byId` map, and the requested
 * group id) so it returns a referentially-stable List across dispatches — the component does not need
 * its own equality function.
 *
 * @param {RootState} state The RootState
 * @param {{ productOptionGroupId: number }} props The option group whose options to load
 * @returns {List<ProductOptionRecord>} The ordered list of resolved options
 */
export const optionsForGroup = createSelector(
  [
    byId,
    // Lazy-wrapped to defer the reference past module load: productOption.selectors and this module
    // import each other, so a bare `productOptionsById` input is `undefined` when this file is
    // evaluated first in the cycle. Resolving it at call-time breaks the eval-time circular dep.
    (state: RootState) => productOptionsById(state),
    (_state: RootState, props: { productOptionGroupId: number }) => props.productOptionGroupId,
  ],
  (groupsById, optionsById, productOptionGroupId): List<ProductOptionRecord> => {
    const group = groupsById.get(productOptionGroupId);
    if (!group) {
      return EMPTY_OPTIONS;
    }

    return group.option_ids
      .map((id: number) => optionsById.get(id))
      .filter((option: Nullable<ProductOptionRecord>): option is ProductOptionRecord => Boolean(option));
  },
);

/**
 * Map of `optionGroupId -> whether the line item has a non-destroyed selected option in that group`,
 * for the product's default-type option groups. Drives the option-group tab selected indicator.
 *
 * Memoized over stable store slices (the groups selector, the option `byId` map, and the line item's
 * `option_ids`) so it returns a referentially-stable object across dispatches — the component does not
 * need its own equality function.
 *
 * @param {RootState} state The RootState
 * @param {{ productId: Nullable<number>; estimateLineItemOptionIds: List<number> }} props The line item's product and its option ids
 * @returns {Record<number, boolean>} optionGroupId -> has a non-destroyed selected option
 */
export const groupSelectionIndicatorsForProduct = createSelector(
  [
    (state: RootState, props: { productId: Nullable<number>; estimateLineItemOptionIds: List<number> }) =>
      defaultProductOptionGroupsForProduct(state, { productId: props.productId }),
    estimateLineItemOptionById,
    (_state: RootState, props: { estimateLineItemOptionIds: List<number> }) => props.estimateLineItemOptionIds,
  ],
  (groups, optionsById: Map<number, EstimateLineItemOptionRecord>, optionIds): Record<number, boolean> => {
    const selectedGroupIds = new Set<number>();
    optionIds.forEach((id) => {
      const option = optionsById.get(id);
      if (option && !option._destroy) {
        selectedGroupIds.add(option.product_option_group_id);
      }
    });

    return groups.reduce<Record<number, boolean>>((acc, group) => {
      acc[group.id] = selectedGroupIds.has(group.id);
      return acc;
    }, {});
  },
);
